import React, { useEffect, useRef, useState } from "react";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type {
  InputRef,
  PopconfirmProps,
  TableColumnsType,
  TableColumnType,
} from "antd";
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { AppDispatch, RootState } from "../store/store";
import { useDispatch } from "react-redux";
import {
  deleteProject,
  fetchProjectDetails,
  fetchProjects,
  setSelectedProjects,
} from "../store/slices/projectSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ProjectInterface, TrancheInterface } from "../domaine/projectModel";
import { hover } from "@testing-library/user-event/dist/hover";
import styled from "styled-components";
import { fetchUsersWithProjects } from "../store/slices/userSlice";

const CustomTag = styled(Tag)`
  cursor: pointer;
  &:hover {
    background-color: #42005a;
    color: white;
  }
`;

interface DataType {
  id: number;
  nom_du_projet: string;
  titre_foncier: string;
  numero_de_permis_de_construire: string;
  surface_fonciere: number;
  surface_construite: number;
  surface_vendable: number;
  promoteur: null | string;
  type_realisation: string;
  nbr_tranches: number;
  is_big_project: boolean;
  etat_projet: string;
  created_at: string;
  updated_at: string;
  tranches: TrancheInterface[];
}

type DataIndex = keyof DataType;

interface Props {
  selectedRowKeys: any;
  setSelectedRowKeys: any;
  setSelectedRows: any;
}

const ProjectsTable: React.FC<Props> = ({
  selectedRowKeys,
  setSelectedRowKeys,
  setSelectedRows,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { projects: data, loading } = useSelector(
    (state: RootState) => state.projects
  );
  const isLoading: boolean =
    loading !== "idle" && loading !== "succeeded" && loading !== "failed";
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<ProjectInterface | null>(null);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
      // Dispatch to Redux store or use context to make this available to other components
      dispatch(setSelectedProjects(selectedRows));
    },
  };
  const handleShowModal = (record: ProjectInterface) => {
    setSelectedProject(record);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedProject(null);
  };

  const confirm = async (id: number) => {
    await dispatch(deleteProject(id))
      .unwrap()
      .then(async () => {
        await dispatch(fetchProjects());
        toast.success("Projet supprimé avec succés");
        await dispatch(fetchUsersWithProjects());
      })
      .catch((error: any) => {
        console.error("Error deleting projet:", error);
        toast.error("Une erreur est survenue.");
      });
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  const handleEdit = async (id: number) => {
    await dispatch(fetchProjectDetails(id));
    navigate("/projectDetails");
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Recherche`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          ></Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
            icon={<ReloadOutlined />}
          ></Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
            style={{ color: "#42005a" }}
          >
            Filtrer
          </Button>
          <Button
            type="link"
            size="small"
            style={{ color: "#42005a" }}
            onClick={() => {
              close();
            }}
          >
            Fermer
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: React.Key | boolean, record: DataType): boolean =>
      typeof record[dataIndex] === "string" &&
      (record[dataIndex] as string)
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: TableColumnsType<DataType> = [
    {
      title: "Nom du projet",
      dataIndex: "nom_du_projet",
      key: "nom_du_projet",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),

      fixed: "left",
      ...getColumnSearchProps("nom_du_projet"),
    },

    {
      title: "Type de réalisation",
      dataIndex: "type_realisation",
      key: "type_realisation",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),

      responsive: ["md"],
    },
    {
      title: "Nombre de tranches",
      dataIndex: "nbr_tranches",
      key: "nbr_tranches",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      responsive: ["md"],
      render: (_, record) => {
        // If type_realisation is "En totalite", show a dash.
        if (record.type_realisation === "En totalité") {
          return (
            <Flex style={{ width: "100%" }} justify="center">
              <p>-</p>
            </Flex>
          );
        }
        // Otherwise, render clickable text to show modal
        return (
          <Flex align="center" style={{ width: "100%" }} justify={"center"}>
            <CustomTag onClick={() => handleShowModal(record)}>
              {record.nbr_tranches} tranches
            </CustomTag>
          </Flex>
        );
      },
    },
    {
      title: "Etat du projet",
      dataIndex: "etat_projet",
      key: "etat_projet",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      responsive: ["md"],
      render: (etat_projet) => {
        let color;
        let label;

        // Assign label and color based on the value of etat_projet
        switch (etat_projet) {
          case "non visité":
            color = "#f50";
            label = "Non Visité";
            break;
          case "actualisé":
            color = "#87d068";
            label = "Actualisé";
            break;
          // case "partiellement actualisé":
          //   color = "gold";
          //   label = "Partiellement Actualisé";
          //   break;
          case "à actualiser":
            color = "#e0a333";
            label = "À Actualiser";
            break;
          default:
            color = "default";
            label = "Inconnu";
        }

        return (
          <Flex align="center" style={{ width: "100%" }} justify={"center"}>
            <Tag style={{ textAlign: "center" }} color={color}>
              {label}
            </Tag>
          </Flex>
        );
      },
      filters: [
        { text: "Non Visité", value: "non visité" },
        { text: "Actualisé", value: "actualisé" },
        // { text: "Partiellement Actualisé", value: "partiellement actualisé" },
        { text: "À Actualiser", value: "à actualiser" },
      ],
      onFilter: (value: any, record) => record.etat_projet.indexOf(value) === 0,
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      responsive: ["md"],
      render: (_, record) => {
        return (
          <Flex align="center" style={{ width: "100%" }} justify={"center"}>
            <p>{new Date(record.created_at).toLocaleDateString()}</p>
          </Flex>
        );
      },
    },
    {
      title: "Opérations",
      key: "operation",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),

      fixed: "right",
      render: (_, record) => (
        <Flex style={{ width: "100%" }} justify="center">
          <Button
            size="small"
            style={{ marginRight: "10px" }}
            variant="solid"
            color="primary"
            onClick={() => {
              handleEdit(record.id);
            }}
          >
            <EyeOutlined />
          </Button>

          <Popconfirm
            title="Supprimer ce projet"
            description="Etes-vous sûr ?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Oui"
            cancelText="Non"
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
          >
            <Button size="small" type="primary" danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Flex>
      ),
    },
  ];
  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  return (
    <>
      <Modal
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
      >
        {selectedProject && selectedProject.tranches && (
          <List
            style={{ marginTop: "40px" }}
            grid={{ gutter: 16, column: 1 }}
            dataSource={selectedProject.tranches}
            renderItem={(tranche) => (
              <List.Item key={tranche.id}>
                <Card
                  title={tranche.name}
                  bordered={false}
                  style={{ border: "2px solid #42005a" }}
                >
                  <p style={{ fontSize: 14, color: "#555" }}>
                    <ClockCircleOutlined style={{ marginRight: 8 }} />
                    Créé le :{" "}
                    {new Date(tranche.created_at).toLocaleDateString()}
                  </p>
                  <Row justify={"start"} style={{ width: "100%",marginTop:"10px" }}>
                    {
                      tranche.suivi_initial ? (
                        tranche.is_updated ? (
                          <>
                          <p style={{ fontSize: 14, color: "#555",marginRight:"10px" }}>Statut : </p><Tag color={"#87d068"}>Actualisé</Tag>
                          </>
                        ) : !tranche.is_visited ? (
                          <>
                          <p style={{ fontSize: 14, color: "#555",marginRight:"10px" }}>Statut : </p><Tag color={"#f50"}>Non Visité</Tag>
                          </>
                          
                        ) : (
                          <> <p style={{ fontSize: 14, color: "#555",marginRight:"10px" }}>Statut : </p><Tag color={"#e0a333"}>A actualiser</Tag></>
                         
                        )
                      ) : (
                        <Alert  showIcon message="Aucun suivi à réaliser (commercialisation achevée avant les visites)" type="info" />
                      )
                    }

                    
                  </Row>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Modal>

      <CustomTable
        size="small"
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        columns={columns}
        dataSource={data}
        scroll={{ x: "max-content" }}
        style={{ padding: "0 10px" }}
        rowKey={(record) => record.id}
        loading={isLoading}
      />
    </>
  );
};

const CustomTable = styled(Table<DataType>)`
  &
    .ant-spin-nested-loading
    .ant-spin-container
    .ant-table
    .ant-table-container
    .ant-table-content
    table
    thead
    th
    .ant-table-filter-column
    .ant-dropdown-trigger {
    color: #fff;
  }
`;

export default ProjectsTable;
